import { useCallback, useEffect, useState } from "react";
import { adminAttendanceService } from "@/services/adminAttendanceService";
import ToastHelper from "@/utils/toastHelper";
import { parseCalendarDate } from "@/utils/dateUtils";

const STATUS_OPTIONS = [
  { id: 1, label: "Present" },
  { id: 2, label: "Absent" },
  { id: 3, label: "Late" },
  { id: 4, label: "Excused" },
  { id: 5, label: "Left Early" },
];

const STATUS_STYLES = {
  Present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Absent: "bg-rose-50 text-rose-700 border-rose-200",
  Late: "bg-amber-50 text-amber-700 border-amber-200",
  Excused: "bg-sky-50 text-sky-700 border-sky-200",
  "Left Early": "bg-violet-50 text-violet-700 border-violet-200",
  "Not Marked": "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminAttendance() {
  const [filters, setFilters] = useState({ classDate: "", campusId: "", courseId: "", studentName: "", page: 1, pageSize: 10 });
  const [data, setData] = useState({ records: [], courses: [], campuses: [], totalRecords: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminAttendanceService.getAttendance({
        ...filters,
        campusId: filters.campusId || undefined,
        courseId: filters.courseId || undefined,
        classDate: filters.classDate || undefined,
      });
      setData(result);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to load attendance");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const updateFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value, page: name === "page" ? value : 1 }));

  const saveStatus = async (row, attendanceStatusId) => {
    const key = `${row.courseScheduleId}-${row.studentId}`;
    setSavingKey(key);
    try {
      await adminAttendanceService.saveAttendance({
        attendanceId: row.attendanceId,
        courseScheduleId: row.courseScheduleId,
        studentId: row.studentId,
        studentBookingId: row.studentBookingId,
        enrollmentId: row.enrollmentId,
        attendanceStatusId,
        checkInTime: row.checkInTime,
        checkOutTime: row.checkOutTime,
        remarks: row.remarks || "",
      });
      ToastHelper.success("Attendance saved successfully");
      await loadAttendance();
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save attendance");
    } finally {
      setSavingKey("");
    }
  };

  const resetFilters = () => setFilters({ classDate: "", campusId: "", courseId: "", studentName: "", page: 1, pageSize: 10 });
  const totalPages = Math.max(1, Number(data.totalPages || 0));

  return <div className="space-y-5" data-testid="attendance-page">
    <div>
      <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Attendance</h1>
      <p className="text-sm text-slate-500">After a class finishes, an administrator records each booked student's attendance.</p>
    </div>

    <div className="wc-card grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      <label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Class date</span><input type="date" className="wc-input" value={filters.classDate} onChange={(e) => updateFilter("classDate", e.target.value)} /></label>
      <FilterSelect label="Location" value={filters.campusId} options={data.campuses} onChange={(value) => updateFilter("campusId", value)} />
      <FilterSelect label="Course" value={filters.courseId} options={data.courses} onChange={(value) => updateFilter("courseId", value)} />
      <label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Student name</span><input className="wc-input" placeholder="Search student" value={filters.studentName} onChange={(e) => updateFilter("studentName", e.target.value)} /></label>
      <div className="flex items-end"><button className="wc-btn-outline w-full" onClick={resetFilters}>Clear filters</button></div>
    </div>

    <div className="wc-card overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>
            <th className="px-4 py-3">Class</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Mark attendance</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading attendance...</td></tr> : data.records?.length ? data.records.map((row) => {
              const rowKey = `${row.courseScheduleId}-${row.studentId}`;
              const futureClass = isFutureClass(row.classDate);
              return <tr key={rowKey} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-[#0A2540]">{row.classCode}</td>
                <td className="px-4 py-3">{formatDate(row.classDate)}</td>
                <td className="px-4 py-3">{row.campusName}</td>
                <td className="px-4 py-3">{row.courseName}</td>
                <td className="px-4 py-3 font-semibold">{row.studentName}</td>
                <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[row.attendanceStatus] || STATUS_STYLES["Not Marked"]}`}>{row.attendanceStatus}</span></td>
                <td className="px-4 py-3"><select className="wc-input min-w-36" disabled={futureClass || savingKey === rowKey} value={row.attendanceStatusId || ""} onChange={(e) => saveStatus(row, Number(e.target.value))}><option value="">{futureClass ? "Not available yet" : "Select status"}</option>{!futureClass && STATUS_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select><div className="mt-1 text-xs text-slate-400">{futureClass ? "Attendance can only be marked on or after the scheduled class date." : "Attendance can be updated"}</div></td>
              </tr>;
            }) : <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No attendance records found.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-slate-500">Showing {data.totalRecords ? ((filters.page - 1) * filters.pageSize) + 1 : 0}-{Math.min(filters.page * filters.pageSize, data.totalRecords || 0)} of {data.totalRecords || 0}</div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-slate-500">Rows <select className="ml-1 rounded-md border border-slate-300 px-2 py-1" value={filters.pageSize} onChange={(e) => setFilters((current) => ({ ...current, page: 1, pageSize: Number(e.target.value) }))}>{[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
          <button className="wc-btn-outline py-1.5" disabled={filters.page <= 1} onClick={() => updateFilter("page", filters.page - 1)}>Previous</button>
          <span className="text-sm text-slate-600">Page {filters.page} of {totalPages}</span>
          <button className="wc-btn-outline py-1.5" disabled={filters.page >= totalPages} onClick={() => updateFilter("page", filters.page + 1)}>Next</button>
        </div>
      </div>
    </div>
  </div>;
}

function FilterSelect({ label, value, options, onChange }) {
  return <label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span><select className="wc-input" value={value} onChange={(e) => onChange(e.target.value)}><option value="">All {label.toLowerCase()}s</option>{(options || []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>;
}

function formatDate(value) {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function isFutureClass(value) {
  const classDate = parseCalendarDate(value);
  if (!classDate || Number.isNaN(classDate.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  classDate.setHours(0, 0, 0, 0);
  return classDate > today;
}
